import React, { useState, useEffect } from 'react'
import { 
  Users, UserPlus, Edit2, Trash2, Shield, Star, 
  Lock, Unlock, Mail, Phone, Calendar, Check, X, Search, CheckCircle
} from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'
import { format, parseISO } from 'date-fns'
import { UserEditorComplete } from './UserEditorComplete'

interface User {
  id: string
  username: string
  display_name: string
  email?: string
  phone_number?: string
  is_parent: boolean
  is_child?: boolean
  login_enabled?: boolean
  color_theme: string
  avatar_url?: string
  created_at: string
  last_login?: string
  last_login_method?: string
  total_points?: number
  chores_completed?: number
  notification_preferences?: {
    push: boolean
    email: boolean
    sms: boolean
  }
  birthdate?: string
  grade?: string
  school?: string
  school_id?: string
  school_name?: string
  primary_place_id?: string
  oauth_accounts?: any[]
  // Transportation fields
  transport_mode?: 'bus' | 'parent_car' | 'drives_self' | 'friend_pickup' | null
  bus_number?: string
  bus_pickup_time?: string
  bus_dropoff_time?: string
  car_pickup_time?: string
  car_dropoff_time?: string
  school_start_time?: string
  school_end_time?: string
  special_friday_arrangement?: string
  // OAuth fields
  google_email?: string
  oauth_provider?: string
}

export const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filter, setFilter] = useState<'all' | 'parents' | 'kids' | 'inactive'>('all')
  const [showAddUser, setShowAddUser] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [schools, setSchools] = useState<{ id: string; name: string }[]>([])
  const [newUser, setNewUser] = useState({
    username: '',
    display_name: '',
    email: '',
    password: '',
    is_parent: false,
    color_theme: '#3B82F6'
  })

  useEffect(() => {
    loadUsers()
    loadSchools()
  }, [])

  const loadSchools = async () => {
    try {
      const data = await apiClient.get('/api/schools/list')
      setSchools(Array.isArray(data) ? data : [])
    } catch {
      setSchools([])
    }
  }

  const loadUsers = async () => {
    setLoading(true)
    try {
      // Use the basic users endpoint which exists
      const data = await apiClient.get('/api/users')
      // Backend returns {family_id, family_name, users: [...], count}
      // Extract the users array from the response
      setUsers(Array.isArray(data) ? data : (data.users || []))
    } catch (error) {
      console.error('Failed to load users:', error)
      toast.error('Failed to load users')
      setUsers([]) // Set to empty array on error
    } finally {
      setLoading(false)
    }
  }

  const handleCreateUser = async () => {
    try {
      await apiClient.post('/api/users', newUser)
      toast.success('User created successfully')
      setShowAddUser(false)
      setNewUser({
        username: '',
        display_name: '',
        email: '',
        password: '',
        is_parent: false,
        color_theme: '#3B82F6'
      })
      loadUsers()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to create user')
    }
  }

  const handleUpdateUser = async (userId: string, updates: Partial<User>) => {
    try {
      await apiClient.patch(`/api/users/${userId}`, updates)
      toast.success('User updated successfully')
      setEditingUser(null)
      loadUsers()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update user')
    }
  }

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Are you sure you want to delete this user?')) return
    
    try {
      await apiClient.delete(`/api/users/${userId}`)
      toast.success('User deleted successfully')
      loadUsers()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete user')
    }
  }

  const handleResetPassword = async (userId: string) => {
    const newPassword = prompt('Enter new password:')
    if (!newPassword) return

    try {
      await apiClient.post(`/api/users/${userId}/reset-password`, { 
        new_password: newPassword 
      })
      toast.success('Password reset successfully')
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to reset password')
    }
  }

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          user.username.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesFilter = filter === 'all' ||
                          (filter === 'parents' && user.is_parent) ||
                          (filter === 'kids' && !user.is_parent) ||
                          (filter === 'inactive' && !user.is_active)
    
    return matchesSearch && matchesFilter
  })

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
        <p className="text-gray-600">Manage system users and permissions</p>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="all">All Users</option>
              <option value="parents">Parents Only</option>
              <option value="kids">Kids Only</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <button
            onClick={() => setShowAddUser(true)}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add User
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="hidden lg:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contact
              </th>
              <th className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Login
              </th>
              <th className="hidden xl:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Linked
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredUsers.map(user => (
              <tr key={user.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                      style={{ backgroundColor: user.color_theme }}
                    >
                      {user.display_name[0]}
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {user.display_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        @{user.username}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {user.is_parent ? (
                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                      <Shield className="w-3 h-3 mr-1" />
                      Parent
                    </span>
                  ) : (
                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      <Star className="w-3 h-3 mr-1" />
                      Kid
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {user.login_enabled !== false ? (
                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      Active
                    </span>
                  ) : (
                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                      Inactive
                    </span>
                  )}
                </td>
                <td className="hidden lg:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div>
                    {user.email && (
                      <div className="text-xs">{user.email}</div>
                    )}
                    {user.phone_number && (
                      <div className="text-xs">{user.phone_number}</div>
                    )}
                    {!user.email && !user.phone_number && (
                      <span className="text-gray-400">No contact info</span>
                    )}
                  </div>
                </td>
                <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user.last_login ? format(parseISO(user.last_login), 'MMM d, h:mm a') : 'Never'}
                </td>
                <td className="hidden xl:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user.google_linked && (
                    <span className="inline-flex items-center text-xs">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-1" />
                      Google
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end space-x-2">
                    <button
                      onClick={() => setEditingUser(user)}
                      className="text-blue-600 hover:text-blue-900"
                      title="Edit user details"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleResetPassword(user.id)}
                      className="text-yellow-600 hover:text-yellow-900"
                      title="Reset password"
                    >
                      <Lock className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleUpdateUser(user.id, { is_active: !user.is_active })}
                      className="text-gray-600 hover:text-gray-900"
                      title={user.is_active ? 'Deactivate user' : 'Activate user'}
                    >
                      {user.is_active ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => handleDeleteUser(user.id)}
                      className="text-red-600 hover:text-red-900"
                      title="Delete user"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* Add User Modal */}
      {showAddUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Add New User</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Username</label>
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Display Name</label>
                <input
                  type="text"
                  value={newUser.display_name}
                  onChange={(e) => setNewUser({ ...newUser, display_name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email (optional)</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Password</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Color Theme</label>
                <input
                  type="color"
                  value={newUser.color_theme}
                  onChange={(e) => setNewUser({ ...newUser, color_theme: e.target.value })}
                  className="w-full h-10"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={newUser.is_parent}
                  onChange={(e) => setNewUser({ ...newUser, is_parent: e.target.checked })}
                  className="mr-2"
                />
                <label className="text-sm">Is Parent/Admin</label>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowAddUser(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateUser}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Create User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal - Use Complete Editor */}
      {editingUser && (
        <UserEditorComplete
          userId={editingUser.id}
          userData={editingUser}
          onClose={() => setEditingUser(null)}
          onSave={() => {
            setEditingUser(null)
            loadUsers()
          }}
        />
      )}

      {/* Old Edit User Modal - Hidden but kept for reference */}
      {false && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Edit User</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Display Name</label>
                <input
                  type="text"
                  value={editingUser.display_name}
                  onChange={(e) => setEditingUser({ ...editingUser, display_name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input
                  type="email"
                  value={editingUser.email || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Phone</label>
                <input
                  type="tel"
                  value={editingUser.phone || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="555-555-5555"
                />
              </div>
              {!editingUser.is_parent && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1">Birthdate</label>
                    <input
                      type="date"
                      value={editingUser.birthdate || ''}
                      onChange={(e) => setEditingUser({ ...editingUser, birthdate: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Grade</label>
                    <input
                      type="text"
                      value={editingUser.grade || ''}
                      onChange={(e) => setEditingUser({ ...editingUser, grade: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="e.g., 8th Grade"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">School</label>
                    <input
                      type="text"
                      list="school-options"
                      value={editingUser.school || ''}
                      onChange={(e) => {
                        const match = schools.find(s => s.name === e.target.value)
                        setEditingUser({
                          ...editingUser,
                          school: e.target.value || undefined,
                          school_id: match?.id || undefined
                        })
                      }}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Type or select a school"
                    />
                    <datalist id="school-options">
                      {schools.map(s => (
                        <option key={s.id} value={s.name} />
                      ))}
                    </datalist>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Transportation Mode</label>
                    <select
                      value={editingUser.transport_mode || ''}
                      onChange={(e) => setEditingUser({ ...editingUser, transport_mode: e.target.value as any })}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="">Not Set</option>
                      <option value="bus">School Bus</option>
                      <option value="parent_car">Parent Car</option>
                      <option value="drives_self">Drives Self</option>
                      <option value="friend_pickup">Friend Pickup</option>
                    </select>
                  </div>
                  {editingUser.transport_mode === 'bus' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium mb-1">Bus Number</label>
                        <input
                          type="text"
                          value={editingUser.bus_number || ''}
                          onChange={(e) => setEditingUser({ ...editingUser, bus_number: e.target.value })}
                          className="w-full px-3 py-2 border rounded-lg"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-sm font-medium mb-1">Pickup Time</label>
                          <input
                            type="time"
                            value={editingUser.bus_pickup_time || ''}
                            onChange={(e) => setEditingUser({ ...editingUser, bus_pickup_time: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Dropoff Time</label>
                          <input
                            type="time"
                            value={editingUser.bus_dropoff_time || ''}
                            onChange={(e) => setEditingUser({ ...editingUser, bus_dropoff_time: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        </div>
                      </div>
                    </>
                  )}
                  {editingUser.transport_mode === 'parent_car' && (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-sm font-medium mb-1">Pickup Time</label>
                        <input
                          type="time"
                          value={editingUser.car_pickup_time || ''}
                          onChange={(e) => setEditingUser({ ...editingUser, car_pickup_time: e.target.value })}
                          className="w-full px-3 py-2 border rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Dropoff Time</label>
                        <input
                          type="time"
                          value={editingUser.car_dropoff_time || ''}
                          onChange={(e) => setEditingUser({ ...editingUser, car_dropoff_time: e.target.value })}
                          className="w-full px-3 py-2 border rounded-lg"
                        />
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium mb-1">Friday Special Arrangement</label>
                    <input
                      type="text"
                      value={editingUser.special_friday_arrangement || ''}
                      onChange={(e) => setEditingUser({ ...editingUser, special_friday_arrangement: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="e.g., Early pickup for sports"
                    />
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">Color Theme</label>
                <input
                  type="color"
                  value={editingUser.color_theme}
                  onChange={(e) => setEditingUser({ ...editingUser, color_theme: e.target.value })}
                  className="w-full h-10"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium">Notification Preferences</label>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editingUser.notification_preferences?.push || false}
                    onChange={(e) => setEditingUser({ 
                      ...editingUser, 
                      notification_preferences: {
                        ...editingUser.notification_preferences,
                        push: e.target.checked
                      } as any
                    })}
                    className="mr-2"
                  />
                  <label className="text-sm">Push Notifications</label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editingUser.notification_preferences?.email || false}
                    onChange={(e) => setEditingUser({ 
                      ...editingUser, 
                      notification_preferences: {
                        ...editingUser.notification_preferences,
                        email: e.target.checked
                      } as any
                    })}
                    className="mr-2"
                  />
                  <label className="text-sm">Email Notifications</label>
                </div>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={editingUser.is_parent}
                  onChange={(e) => setEditingUser({ ...editingUser, is_parent: e.target.checked })}
                  className="mr-2"
                />
                <label className="text-sm">Is Parent/Admin</label>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setEditingUser(null)}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={() => handleUpdateUser(editingUser.id, editingUser)}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}